import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import api from "../api/axiosInstance";
 import "../style/passbook.css";


const CustomerSearch = ({ onSelectCustomer, selectedCustomer }) => {
  const [searchTerm, setSearchTerm] = useState(selectedCustomer?.name || "");
  const [customers, setCustomers] = useState([]);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Sync searchTerm when selectedCustomer changes externally (e.g. from challan load)
  useEffect(() => {
    if (selectedCustomer?.name) {
      setSearchTerm(selectedCustomer.name);
    } else if (!selectedCustomer) {
      setSearchTerm("");
    }
  }, [selectedCustomer]);

  // 🔥 Call API
  const searchCustomers = useCallback(async (name) => {
    if (!name || selectedCustomer) {
      setCustomers([]);
      return;
    }

    try {
      const response = await api.get(
        "/customers/searchCustomers",
        {
          params: { name: name }
        }
      );

      setCustomers(response.data || []);
    } catch (error) {
      console.error("Search error:", error);
    }
  }, [selectedCustomer]);

  // 🔥 Debounce (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      searchCustomers(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, searchCustomers]);

  // Clear dropdown when customer is selected
  useEffect(() => {
    if (selectedCustomer) {
      setCustomers([]);
    }
  }, [selectedCustomer]);

  // Calculate dropdown position
  useEffect(() => {
    if (customers.length > 0 && triggerRef.current) {
      const updatePosition = () => {
        const rect = triggerRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;
        const gap = 6;
        const viewportPadding = 8;

        const availableBelow = Math.max(
          0,
          windowHeight - rect.bottom - viewportPadding - gap
        );
        const availableAbove = Math.max(
          0,
          rect.top - viewportPadding - gap
        );

        const preferredHeight = Math.min(320, customers.length * 60 + 16);
        const showAbove =
          availableBelow < Math.min(preferredHeight, 160) &&
          availableAbove > availableBelow;

        const maxHeight = Math.max(
          1,
          Math.min(preferredHeight, showAbove ? availableAbove : availableBelow)
        );

        const width = Math.min(
          Math.max(rect.width, 320),
          windowWidth - viewportPadding * 2
        );

        let left = rect.left;
        if (left + width > windowWidth - viewportPadding) {
          left = windowWidth - width - viewportPadding;
        }
        left = Math.max(viewportPadding, left);

        let top = showAbove
          ? rect.top - maxHeight - gap
          : rect.bottom + gap;

        top = Math.max(
          viewportPadding,
          Math.min(top, windowHeight - maxHeight - viewportPadding)
        );

        setDropdownStyle({
          position: 'fixed',
          left,
          width,
          maxHeight,
          top,
          zIndex: 'var(--z-dropdown)'
        });
      };

      updatePosition();

      // Add scroll listener to update position during scroll
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [customers.length]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        setCustomers([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (customer) => {
    setSearchTerm(customer.name);
    setCustomers([]);
    onSelectCustomer(customer); // send full object to Passbook
  };

  const dropdownContent = customers.length > 0 && !selectedCustomer ? (
    <div className="dropdown-list" style={dropdownStyle} ref={dropdownRef}>
      {customers.map((customer) => (
        <div
          key={customer.id}
          className="dropdown-item"
          onClick={() => handleSelect(customer)}
        >
          <div className="customer-name">{customer.name}</div>
          <div className="customer-contact">
            {customer.contactNumber || 'No contact'}
          </div>
          <div className="customer-email">
            {customer.email || 'No email'}
          </div>
        </div>
      ))}
    </div>
  ) : null;

    
  return (
  <div className="customer-search">
    <label>Customer</label>

    <div className="dropdown-container">
      <div className="customer-input-wrapper" ref={triggerRef}>
        <input
          type="text"
          placeholder="Search customer..."
          value={searchTerm} 
          onChange={(e) => {
            setSearchTerm(e.target.value);
            onSelectCustomer(null);
          }}
          className="customer-input"
        />
      </div>
      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  </div>
);
};

export default CustomerSearch;
